import { AndroidOutlined, WindowsOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Flex, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';

import { authUpdateMe, getAuthMeQueryKey, useAuthLogin } from '@/api/generated/endpoints/auth/auth';
import {
  useDesktopGetAndroidRelease,
  useDesktopGetRelease,
} from '@/api/generated/endpoints/desktop/desktop';
import type { LoginIn, MeOut } from '@/api/generated/model';
import { safeNext } from '@/auth/next';
import { useCurrentUser } from '@/auth/useCurrentUser';
import type { Language } from '@/i18n';
import { LanguageSwitch } from '@/i18n/LanguageSwitch';
import { problemMessage } from '@/i18n/problem';
import { useIsMobile } from '@/lib/useIsMobile';
import { androidApp, isDesktop, serverUrl } from '@/runtime';

/** Ссылка на установщик программы для Windows (T-185) — если выпуск лежит на сервере. */
function WindowsDownload() {
  const { t } = useTranslation();
  const release = useDesktopGetRelease({ query: { retry: false, staleTime: Infinity } });
  // 204 — выпусков на сервере нет: тело пустое.
  const data = release.data && typeof release.data === 'object' ? release.data : null;
  if (!data) return null;
  return (
    <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
      <WindowsOutlined />{' '}
      <Typography.Link href={data.url} download>
        {t('update.download', { version: data.version })}
      </Typography.Link>
      <br />
      {t('update.downloadHint')}
    </Typography.Paragraph>
  );
}

/** Браузер на Android, а не наше приложение — предложить приложение (T-173). */
const isAndroidBrowser = () => /Android/i.test(navigator.userAgent) && androidApp() === null;

function AndroidDownload() {
  const { t } = useTranslation();
  const release = useDesktopGetAndroidRelease({ query: { retry: false, staleTime: Infinity } });
  const data = release.data && typeof release.data === 'object' ? release.data : null;
  if (!data) return null;
  return (
    <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
      <AndroidOutlined />{' '}
      <Typography.Link href={data.url} download>
        {t('update.androidApp', { version: data.version })}
      </Typography.Link>
      <br />
      {t('update.androidHint')}
    </Typography.Paragraph>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const next = safeNext(params.get('next'));
  // Язык, явно выбранный на этой странице, — свежее решение человека: сохранить в профиль.
  // Без явного выбора главнее профиль (на общем компьютере в браузере язык прошлого входа).
  const [chosen, setChosen] = useState<Language | null>(null);
  const login = useAuthLogin({
    mutation: {
      onSuccess: async (me) => {
        let profile: MeOut = me;
        if (chosen && chosen !== me.locale) {
          profile = await authUpdateMe({ locale: chosen }).catch(() => me);
        }
        queryClient.setQueryData(getAuthMeQueryKey(), profile);
        void navigate(next, { replace: true });
      },
    },
  });

  if (user) return <Navigate to={next} replace />;

  return (
    <div
      style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', background: '#f5f5f5' }}
    >
      <Card style={{ width: 360 }}>
        <Flex justify="space-between" align="start">
          <Typography.Title level={4} style={{ marginTop: 0 }}>
            {t('app.title')}
          </Typography.Title>
          <LanguageSwitch saveToProfile={false} onChanged={setChosen} />
        </Flex>
        <Typography.Paragraph type="secondary">{t('auth.subtitle')}</Typography.Paragraph>
        {login.error && (
          <Alert
            type="error"
            showIcon
            message={problemMessage(login.error)}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form<LoginIn>
          layout="vertical"
          size="middle"
          requiredMark={false}
          onFinish={(values) => {
            login.mutate({ data: values });
          }}
        >
          <Form.Item
            name="login"
            label={t('auth.login')}
            rules={[{ required: true, message: t('auth.loginRequired') }]}
          >
            <Input autoComplete="username" autoFocus />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('auth.password')}
            rules={[{ required: true, message: t('auth.passwordRequired') }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          {isDesktop && (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
              {t('server.current', { url: serverUrl() ?? '' })}{' '}
              <Link to="/server">{t('server.change')}</Link>
            </Typography.Paragraph>
          )}
          <Button type="primary" htmlType="submit" block loading={login.isPending}>
            {t('auth.signIn')}
          </Button>
        </Form>
        {!isDesktop && !mobile && <WindowsDownload />}
        {isAndroidBrowser() && <AndroidDownload />}
      </Card>
    </div>
  );
}
